import { formatTimeSpent } from "@/shared/utils/chess";
import { Box, Typography } from "@mui/material";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";

const flagLabel = (
  m: AnalyzedMoveResult | null,
  includeOpponentEvidence: boolean,
): { label: string; color: string } | null => {
  if (!m) return null;
  if (m.motifs.includes("checkmate")) return { label: "MATE", color: "primary.main" };
  if (!m.isPlayerMove && !includeOpponentEvidence) return null;
  if (m.motifs.includes("opponent_blunder")) return { label: "??", color: "#ef4444" };
  if (m.motifs.includes("opponent_mistake")) return { label: "?", color: "#f59e0b" };
  if (m.isBlunder) return { label: "??", color: "#ef4444" };
  if (m.isMistake) return { label: "?", color: "#f59e0b" };
  return null;
};

export interface MovePairRow {
  moveNumber: number;
  white: AnalyzedMoveResult | null;
  black: AnalyzedMoveResult | null;
  whiteIdx: number;
  blackIdx: number;
}

interface MoveListProps {
  movePairs: MovePairRow[];
  currentIndex: number;
  includeOpponentEvidence?: boolean;
  onSelectIndex: (index: number) => void;
}

/**
 * Two-column (White / Black) move list. Click any ply to jump to it.
 */
export const MoveList = ({
  movePairs,
  currentIndex,
  onSelectIndex,
  includeOpponentEvidence = false,
}: MoveListProps) => (
  <Box sx={{ borderTop: "2px solid rgba(255,255,255,0.1)", px: 1, py: 0.5 }}>
    {movePairs.map((row) => (
      <Box
        key={row.moveNumber}
        sx={{
          display: "grid",
          gridTemplateColumns: "32px 1fr 1fr",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "right", pr: 0.5 }}>
          {row.moveNumber}.
        </Typography>
        {(
          [
            ["white", row.white, row.whiteIdx],
            ["black", row.black, row.blackIdx],
          ] as const
        ).map(([side, m, i]) => {
          const flag = flagLabel(m, includeOpponentEvidence);
          return (
            <Box
              key={side}
              onClick={() => {
                if (m) onSelectIndex(i);
              }}
              sx={{
                px: 0.75,
                py: 0.4,
                borderRadius: 0.5,
                cursor: m ? "pointer" : "default",
                bgcolor: i === currentIndex ? "rgba(240,236,226,0.15)" : "transparent",
                "&:hover": m ? { bgcolor: "rgba(240,236,226,0.08)" } : undefined,
              }}
            >
              {m && (
                <>
                  <Typography variant="body2" sx={{ display: "inline", fontWeight: i === currentIndex ? 700 : 400 }}>
                    {m.san}
                    {flag && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: flag.color,
                          ml: 0.3,
                          fontWeight: 700,
                        }}
                      >
                        {flag.label}
                      </Typography>
                    )}
                  </Typography>
                  {m.timeSpentSeconds != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        fontSize: "0.65rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {formatTimeSpent(m.timeSpentSeconds)}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    ))}
  </Box>
);
